// Screen: "Cosmetic Shop"
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getShopItems, purchaseCosmetic, getInventory, equipCosmetic } from '../apiClient';

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

const CATEGORIES = [
  { key: null, label: 'All' },
  { key: 'border', label: 'Borders' },
  { key: 'card_back', label: 'Card Backs' },
  { key: 'emote', label: 'Emotes' },
  { key: 'table_theme', label: 'Table Themes' },
];

const RARITY_COLORS = {
  common: '#8b949e',
  rare: '#58a6ff',
  epic: '#a855f7',
  legendary: '#d4af37',
};

const TYPE_ICONS = {
  border: '🖼️',
  card_back: '🃏',
  emote: '💬',
  table_theme: '🎨',
};

export default function ShopPage() {
  const { user, token, updateUser, isClubMember } = useAuth();
  const isMobile = useIsMobile();
  const [category, setCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [owned, setOwned] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    const shopData = await getShopItems(token, category);
    setItems(Array.isArray(shopData) ? shopData : []);
    if (token) {
      const inv = await getInventory(token);
      setOwned(Array.isArray(inv) ? inv : []);
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, token]);

  const handlePurchase = async (itemId) => {
    const result = await purchaseCosmetic(token, itemId);
    if (result.error) {
      setMessage(result.error);
    } else {
      setMessage('Purchase successful!');
      updateUser({ playMoneyBalance: result.newBalance });
      loadData();
    }
    setTimeout(() => setMessage(''), 3000);
  };

  const handleEquip = async (itemId) => {
    await equipCosmetic(token, itemId);
    loadData();
  };

  const isOwned = (itemId) => owned.some(o => o.itemId === itemId);
  const isEquipped = (itemId) => owned.some(o => o.itemId === itemId && o.equipped);
  const balance = user?.playMoneyBalance || 0;

  return (
    <div style={{
      padding: isMobile ? '20px 16px 40px' : '40px 24px',
      maxWidth: 700, margin: '0 auto',
      fontFamily: "'Georgia', serif", color: '#e2e8f0'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ fontSize: isMobile ? 24 : 36, fontWeight: 700, margin: 0 }}>Shop</h1>
        <span style={{
          background: '#1c2333', borderRadius: 20, padding: '6px 14px',
          fontSize: 13, fontWeight: 600, color: '#fbbf24',
          display: 'flex', alignItems: 'center', gap: 4,
          border: '1px solid #fbbf2440'
        }}>
          <img src="/bucks-icon.svg" alt="" style={{ width: 14, height: 14 }} />
          {balance.toLocaleString()} Bucks
        </span>
      </div>

      {message && (
        <div style={{
          background: message.includes('error') || message.includes('Insufficient') ? '#3d1f1f' : '#1c3d2a',
          border: `1px solid ${message.includes('error') || message.includes('Insufficient') ? '#f85149' : '#3fb950'}`,
          borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 12,
          color: message.includes('error') || message.includes('Insufficient') ? '#f85149' : '#3fb950'
        }}>
          {message}
        </div>
      )}

      {/* Category tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
        {CATEGORIES.map(c => (
          <button
            key={c.key || 'all'}
            onClick={() => setCategory(c.key)}
            style={{
              background: category === c.key ? '#d4af37' : 'transparent',
              color: category === c.key ? '#0d1117' : '#8b949e',
              border: category === c.key ? 'none' : '1px solid #30363d',
              borderRadius: 6, padding: isMobile ? '6px 10px' : '7px 14px',
              fontSize: isMobile ? 11 : 12, cursor: 'pointer', fontWeight: 600
            }}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Items grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#8b949e' }}>Loading shop...</div>
      ) : items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#8b949e' }}>No items available in this category.</div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr',
          gap: isMobile ? 10 : 14
        }}>
          {items.map(item => {
            const ownedItem = isOwned(item.item_id);
            const equipped = isEquipped(item.item_id);
            const subscriberOnly = item.subscriber_only;
            const locked = subscriberOnly && !isClubMember && !ownedItem;
            const canAfford = !locked && balance >= item.cost_bucks;
            const rarityColor = RARITY_COLORS[item.rarity] || RARITY_COLORS.common;
            const typeIcon = TYPE_ICONS[item.type] || '🎁';

            return (
              <div key={item.item_id} style={{
                background: '#161b22',
                border: equipped ? '2px solid #d4af37' : subscriberOnly ? '1px solid #3fb95040' : '1px solid #30363d',
                borderRadius: 10,
                padding: isMobile ? 10 : 14,
                display: 'flex', flexDirection: 'column',
                position: 'relative',
                opacity: locked ? 0.6 : 1
              }}>
                {/* Subscriber-only badge */}
                {subscriberOnly && (
                  <span style={{
                    position: 'absolute', top: 6, left: 6,
                    fontSize: 8, fontWeight: 700, color: '#3fb950',
                    background: '#1c3d2a', padding: '2px 6px', borderRadius: 4,
                    textTransform: 'uppercase', letterSpacing: 0.5
                  }}>
                    Club
                  </span>
                )}
                {/* Rarity badge */}
                <span style={{
                  position: 'absolute', top: 6, right: 6,
                  fontSize: 9, fontWeight: 700, color: rarityColor,
                  textTransform: 'uppercase', letterSpacing: 0.5
                }}>
                  {item.rarity}
                </span>

                {/* Item preview */}
                <div style={{
                  fontSize: 32, textAlign: 'center', marginBottom: 8,
                  padding: '12px 0', background: '#0d1117', borderRadius: 6
                }}>
                  {typeIcon}
                </div>

                <div style={{ fontSize: isMobile ? 12 : 13, fontWeight: 600, color: '#c9d1d9', marginBottom: 2 }}>
                  {item.name}
                </div>
                <div style={{ fontSize: 10, color: '#8b949e', marginBottom: 8, flex: 1 }}>
                  {item.description}
                </div>

                {/* Price / Action */}
                {ownedItem ? (
                  <button
                    onClick={() => !equipped && handleEquip(item.item_id)}
                    style={{
                      background: equipped ? '#d4af3730' : '#1c2333',
                      color: equipped ? '#d4af37' : '#58a6ff',
                      border: `1px solid ${equipped ? '#d4af37' : '#30363d'}`,
                      borderRadius: 6, padding: '6px 10px', fontSize: 11,
                      cursor: equipped ? 'default' : 'pointer', fontWeight: 600
                    }}
                  >
                    {equipped ? 'Equipped' : 'Equip'}
                  </button>
                ) : locked ? (
                  <button
                    disabled
                    style={{
                      background: '#1a1a2e', color: '#4b5563',
                      border: 'none', borderRadius: 6, padding: '6px 10px',
                      fontSize: 11, cursor: 'not-allowed', fontWeight: 600, opacity: 0.6
                    }}
                  >
                    Club Members Only
                  </button>
                ) : (
                  <button
                    disabled={!canAfford}
                    onClick={() => canAfford && handlePurchase(item.item_id)}
                    style={{
                      background: canAfford ? '#238636' : '#1a1a2e',
                      color: canAfford ? '#fff' : '#4b5563',
                      border: 'none', borderRadius: 6, padding: '6px 10px',
                      fontSize: 11, cursor: canAfford ? 'pointer' : 'not-allowed',
                      fontWeight: 600, opacity: canAfford ? 1 : 0.6,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4
                    }}
                  >
                    <img src="/bucks-icon.svg" alt="" style={{ width: 11, height: 11, opacity: 0.7 }} />
                    {item.cost_bucks} Bucks
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
